import React, { useState, useEffect } from 'react';
import { Check, Star, ArrowRight, Loader2, Building2, Mail, Phone, Globe, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import PageFrame from '../components/PageFrame';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const QUIZ_OPTIONS = [
  { id: 'general-safety', name: 'Truck Safety Knowledge Test' },
  { id: 'accident-prevention', name: 'Accident Prevention Quiz' },
  { id: 'load-securement', name: 'Load Securement Safety Quiz' }
];

const Sponsorship = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [inquiryId, setInquiryId] = useState('');
  
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    quiz_preferences: [],
    message: ''
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/api/sponsorship/packages`);
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setShowForm(true);
  };

  const handleQuizToggle = (quizId) => {
    setFormData(prev => ({
      ...prev,
      quiz_preferences: prev.quiz_preferences.includes(quizId)
        ? prev.quiz_preferences.filter(id => id !== quizId)
        : [...prev.quiz_preferences, quizId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.contact_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/api/sponsorship/inquire`, {
        ...formData,
        package_id: selectedPackage.package_id
      });
      
      if (response.data.success) {
        setInquiryId(response.data.inquiry_id);
        setSubmitted(true);
        toast.success('Inquiry submitted successfully!');
      }
    } catch (error) {
      console.error('Failed to submit inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setSubmitted(false);
    setSelectedPackage(null);
    setFormData({
      company_name: '',
      contact_name: '',
      email: '',
      phone: '',
      website: '',
      quiz_preferences: [],
      message: ''
    });
  };

  return (
    <PageFrame>
      <div className="min-h-screen bg-gray-950">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-transparent" />
          <div className="max-w-6xl mx-auto px-4 relative">
            <div className="text-center mb-12">
              <Badge className="bg-orange-500/20 text-orange-400 mb-4">Sponsorship Opportunities</Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Sponsor Our Safety Quizzes
              </h1>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Get your brand in front of thousands of truck drivers and fleet managers. 
                Your sponsorship helps us promote road safety while giving you valuable exposure.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto mb-12">
              <div className="text-center">
                <p className="text-3xl font-bold text-orange-500">10K+</p>
                <p className="text-gray-400 text-sm">Monthly Quiz Takers</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-orange-500">85%</p>
                <p className="text-gray-400 text-sm">Email Open Rate</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-orange-500">3</p>
                <p className="text-gray-400 text-sm">Available Quizzes</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-white text-center mb-12">Choose Your Package</h2>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {packages.map((pkg) => (
                  <Card 
                    key={pkg.package_id}
                    className={`relative bg-gray-900 border-gray-800 ${
                      pkg.highlight ? 'border-orange-500 ring-2 ring-orange-500/20' : ''
                    }`}
                  >
                    {pkg.highlight && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-orange-500 text-white px-3">
                          <Star className="h-3 w-3 mr-1 inline" />
                          Most Popular
                        </Badge>
                      </div>
                    )}
                    <CardHeader className="text-center pb-2">
                      <CardTitle className="text-white text-xl">{pkg.name}</CardTitle>
                      <CardDescription className="text-gray-500">{pkg.duration}</CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                      <div className="mb-6">
                        <span className="text-4xl font-bold text-white">${pkg.price}</span>
                        <span className="text-gray-500">/{pkg.duration.split(' ')[1]}</span>
                      </div>
                      
                      <ul className="space-y-3 mb-6 text-left">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                            <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      
                      <Button
                        onClick={() => handleSelectPackage(pkg)}
                        className={`w-full ${
                          pkg.highlight 
                            ? 'bg-orange-500 hover:bg-orange-600' 
                            : 'bg-gray-700 hover:bg-gray-600'
                        }`}
                      >
                        Select Package
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-white text-center mb-12">Why Sponsor With Us?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-orange-500" />
                </div>
                <h3 className="text-white font-semibold mb-2">Direct Email Placement</h3>
                <p className="text-gray-400 text-sm">
                  Your brand appears directly in quiz result emails sent to engaged users.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
                  <Building2 className="h-8 w-8 text-orange-500" />
                </div>
                <h3 className="text-white font-semibold mb-2">Targeted Audience</h3>
                <p className="text-gray-400 text-sm">
                  Reach truck drivers and fleet managers actively learning about safety.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
                  <Star className="h-8 w-8 text-orange-500" />
                </div>
                <h3 className="text-white font-semibold mb-2">Brand Association</h3>
                <p className="text-gray-400 text-sm">
                  Associate your brand with truck safety education and professional development.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Inquiry Form Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="bg-gray-900 border-gray-800 max-w-lg max-h-[90vh] overflow-y-auto">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Inquiry Submitted!</h3>
                <p className="text-gray-400 mb-4">
                  Thank you for your interest in the <span className="text-orange-400">{selectedPackage?.name}</span> package.
                </p>
                <p className="text-gray-500 text-sm mb-6">
                  Reference: <span className="text-white font-mono">#{inquiryId}</span>
                </p>
                <p className="text-gray-400 text-sm mb-6">
                  We'll be in touch within 24 hours. Check your email for confirmation.
                </p>
                <Button onClick={resetForm} className="bg-orange-500 hover:bg-orange-600">
                  Done
                </Button>
              </div>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle className="text-white">
                    {selectedPackage?.name} Package Inquiry
                  </DialogTitle>
                  <DialogDescription>
                    Fill out the form below and we'll get back to you within 24 hours.
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Company Name *</label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                        placeholder="Your Company"
                        className="bg-gray-800 border-gray-700 text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Contact Name *</label>
                      <Input
                        value={formData.contact_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                        placeholder="Your Name"
                        className="bg-gray-800 border-gray-700 text-white"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Email *</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="you@company.com"
                      className="bg-gray-800 border-gray-700 text-white"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Phone</label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="(555) 123-4567"
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Website</label>
                      <Input
                        value={formData.website}
                        onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                        placeholder="https://..."
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Preferred Quiz(es) to Sponsor</label>
                    <div className="space-y-2">
                      {QUIZ_OPTIONS.map((quiz) => (
                        <label
                          key={quiz.id}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            formData.quiz_preferences.includes(quiz.id)
                              ? 'bg-orange-500/20 border border-orange-500/50'
                              : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.quiz_preferences.includes(quiz.id)}
                            onChange={() => handleQuizToggle(quiz.id)}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            formData.quiz_preferences.includes(quiz.id)
                              ? 'bg-orange-500 border-orange-500'
                              : 'border-gray-600'
                          }`}>
                            {formData.quiz_preferences.includes(quiz.id) && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <span className="text-white text-sm">{quiz.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Message (Optional)</label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Tell us about your company and sponsorship goals..."
                      className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowForm(false)}
                      className="flex-1 border-gray-700 text-gray-300"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-orange-500 hover:bg-orange-600"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Inquiry'
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </PageFrame>
  );
};

export default Sponsorship;
